import { useMemo } from 'react';

import cx from 'classnames';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';

import { PLACE_ORDER_TYPE, SIDE } from 'constants/enums';
import {
  confirmationHeaderSelector,
  orderTypeSelection,
  sideSelection,
  slippageCheckSelector,
} from 'selectors/placeOrderSelector';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { useAppSelector } from 'storeHooks';
import { ContractType } from 'types/IProducts';

import ConfirmationTable from './ConfirmationTable';
import ConfirmButtons from './ConfirmButtons';
import DontShowConfirmation from './DontShowConfirmation';
import styles from './Modals.module.scss';

const noteLabel = i18next.t('trading:nb');
const didYouIntendToPlaceThisOrder = i18next.t('trading:didYouIntendToPlaceThisOrder');
const confirmYour = i18next.t('trading:confirmYour');

const ConfirmationComponent = () => {
  const { t } = useTranslation(['trading', 'common']);
  const orderType = useAppSelector(orderTypeSelection);
  const selectedProduct = useAppSelector(selectedProductSelector);
  const side = useAppSelector(sideSelection);
  const confirmationHeader = useAppSelector(confirmationHeaderSelector);
  const slippageCheck = useAppSelector(slippageCheckSelector);
  const priceBrandingLabel =
    side === SIDE.BUY
      ? i18next.t('trading:marketOrderPriceBandingMsgBuy')
      : i18next.t('trading:marketOrderPriceBandingMsgSell');

  const confirmationTitle = useMemo(() => {
    switch (orderType) {
      case PLACE_ORDER_TYPE.MARKET:
        return t('trading:marketOrder');
      default:
        return '';
    }
  }, [orderType, t]);

  return (
    (<div
      className={styles.wrapper}
      data-testid="confirmation-modal"
      data-palette="ConfirmationComponent">
      <div className="d-flex">
        <div className={cx(styles.header, 'col-24')} data-testid="confirmation-title">
          {`${confirmYour} ${confirmationTitle}`}
        </div>
      </div>
      <div className={cx('d-flex', styles.initialMargin)}>
        <div className={cx('col-xxs-24', styles.valueText)}>
          <span
            className={cx('capitalize', {
              [styles.buy]: side === SIDE.BUY,
              [styles.sell]: side === SIDE.SELL,
            })}
            data-testid="confirmation-side"
          >
            {`${side} `}
          </span>
          {confirmationHeader}
        </div>
      </div>
      {slippageCheck && (
        <div
          className={styles.fatFingerContainer}
          data-palette="ConfirmationComponent">
          <div className={cx('row', styles.fatFingerSection)}>
            <div className={cx('col-xxs-24', styles.fatText)} data-testid="fatText">
              {didYouIntendToPlaceThisOrder}?
            </div>
            <div className={cx('col-xxs-24', styles.fatReason)}>
              <div className={styles.fatMessage} data-testid="fatMessage">
                {slippageCheck}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={styles.orderDetailsTable} data-testid="orderDetailsTable">
        <ConfirmationTable />
      </div>
      {/* dont show confirmation checkboxes */}
      <DontShowConfirmation />
      <ConfirmButtons />
      {selectedProduct?.contract_type !== ContractType.Spot && (
        <div
          className={cx(styles.label, styles.noteLabel)}
          data-testid="noteLabel"
          data-palette="ConfirmationComponent">
          {noteLabel}: {priceBrandingLabel}
        </div>
      )}
    </div>)
  );
};

export default ConfirmationComponent;
